export interface AnnotationEntityLike {
  type: string;
  value: string;
  start?: number;
  end?: number;
}

export type SpannedAnnotationEntity<T extends AnnotationEntityLike> = T & {
  start: number;
  end: number;
};

export interface AnnotationTextSegment<T extends AnnotationEntityLike> {
  start: number;
  end: number;
  text: string;
  annotations: Array<SpannedAnnotationEntity<T>>;
}

export interface AnnotationSpanLane<T extends AnnotationEntityLike> {
  annotation: SpannedAnnotationEntity<T>;
  lane: number;
}

/**
 * 
 * @param text the text to validate the annotation span against 
 * @param entity the referred annotation entity to validate
 * @returns true if the annotation entity has valid start and end indices and the text slice matches the entity value, false otherwise
 */
export function hasValidAnnotationSpan<T extends AnnotationEntityLike>(
  text: string,
  entity: T
): entity is SpannedAnnotationEntity<T> {
  if (
    typeof entity.start !== "number" ||
    typeof entity.end !== "number" ||
    !Number.isInteger(entity.start) ||
    !Number.isInteger(entity.end)
  ) {
    return false;
  }

  return (
    entity.start >= 0 &&
    entity.end > entity.start &&
    entity.end <= text.length &&
    text.slice(entity.start, entity.end) === entity.value
  );
}


/**
 * 
 * @param text the text that should be split into segments based on the provided annotation spans
 * @param annotations the annotation spans that should be used to split the text into segments
 * @param extraBoundaries further offsets to split at, such as where evidence for a statement starts and ends
 * @returns the resulting overlap-aware segments of the text with corresponding start and end indices, text slice
 */
export function buildAnnotationTextSegments<T extends AnnotationEntityLike>(
  text: string,
  annotations: readonly SpannedAnnotationEntity<T>[],
  extraBoundaries: readonly number[] = []
): Array<AnnotationTextSegment<T>> {
  if (!text) {
    return [];
  }

  const boundaries = new Set<number>([0, text.length, ...extraBoundaries.filter((offset) => offset > 0 && offset < text.length)]);
  annotations.forEach((annotation) => {
    boundaries.add(annotation.start);
    boundaries.add(annotation.end);
  });

  const sortedBoundaries = Array.from(boundaries).sort((left, right) => left - right);
  const segments: Array<AnnotationTextSegment<T>> = [];

  for (let index = 0; index < sortedBoundaries.length - 1; index += 1) {
    const start = sortedBoundaries[index];
    const end = sortedBoundaries[index + 1];
    if (start === undefined || end === undefined || start === end) {
      continue;
    }

    segments.push({
      start,
      end,
      text: text.slice(start, end),
      annotations: annotations.filter(
        (annotation) => annotation.start < end && annotation.end > start
      ),
    });
  }

  return segments;
}

function annotationSpansOverlap<T extends AnnotationEntityLike>(
  left: SpannedAnnotationEntity<T>,
  right: SpannedAnnotationEntity<T>
): boolean {
  return left.start < right.end && left.end > right.start;
}

function annotationSpanLength<T extends AnnotationEntityLike>(
  annotation: SpannedAnnotationEntity<T>
): number {
  return annotation.end - annotation.start;
}

/**
 * Assign visual lanes so that longer overlapping annotations are closer to the text
 * and shorter annotations stack farther away.
 */
export function assignAnnotationSpanLanes<T extends AnnotationEntityLike>(
  annotations: readonly SpannedAnnotationEntity<T>[]
): Array<AnnotationSpanLane<T>> {
  const sortedAnnotations = [...annotations].sort((left, right) => {
    // sort by length first, then by start index, then by end index, then by type
    const lengthDifference = annotationSpanLength(right) - annotationSpanLength(left);
    if (lengthDifference !== 0) {
      return lengthDifference;
    }

    if (left.start !== right.start) {
      return left.start - right.start;
    }

    if (left.end !== right.end) {
      return right.end - left.end;
    }

    return left.type.localeCompare(right.type);
  });
  const placedAnnotations: Array<AnnotationSpanLane<T>> = [];
  const laneByAnnotation = new Map<SpannedAnnotationEntity<T>, number>();

  // Iterate through the sorted annotations and assign lanes based on overlapping annotations. The first annotation is assigned to lane 0, and subsequent annotations are assigned to the next available lane that does not overlap with any previously placed annotations.
  sortedAnnotations.forEach((annotation) => {
    const overlappingAnnotations = placedAnnotations.filter((placed) =>
      annotationSpansOverlap(annotation, placed.annotation)
    );
    const minimumLane =
      overlappingAnnotations.length > 0
        ? Math.max(...overlappingAnnotations.map((placed) => placed.lane)) + 1
        : 0;
    const lane = minimumLane;

    const placed = { annotation, lane };
    placedAnnotations.push(placed);
    laneByAnnotation.set(annotation, lane); // allows to later ask which lane this specific annotation belongs to
  });

  return annotations.map((annotation) => ({
    annotation,
    lane: laneByAnnotation.get(annotation) || 0,
  }));
}
